/**
 * Energy System API Client
 * Communicates with mkey worker energy endpoints
 */

const MKEY_API_URL = import.meta.env.VITE_MKEY_API_URL || 'https://mkey-api-production.fidelismadboat.workers.dev';

function getToken(): string | null {
  try {
    const stored = localStorage.getItem('madboat-auth');
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed?.state?.token || null;
  } catch {
    return null;
  }
}

async function fetchEnergy<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');

  const res = await fetch(`${MKEY_API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || `HTTP ${res.status}`);
  }

  const json = await res.json();
  return json.data ?? json;
}

// ── Types ──

export interface EnergyStatus {
  currentEnergy: number;
  maxEnergy: number;
  regenRateMinutes: number;
  nextRegenAt: number;
  unlimited: boolean;
}

export interface EnergyConfigItem {
  action: string;
  amount: number;
  description: string;
  category: string;
  dailyCap?: number | null;
  frequency?: string;
}

export interface EnergyConfig {
  earn: EnergyConfigItem[];
  spend: EnergyConfigItem[];
  conversionRate: { vibecoin: number; energy: number };
}

export interface EnergyLogEntry {
  id: string;
  action: string;
  amount: number;
  direction: 'earn' | 'spend';
  balance_after: number;
  created_at: number;
}

// ── API Functions ──

export async function getEnergyStatus(): Promise<EnergyStatus> {
  return fetchEnergy<EnergyStatus>('/api/energy/status');
}

export async function getEnergyConfig(): Promise<EnergyConfig> {
  return fetchEnergy<EnergyConfig>('/api/energy/config');
}

export async function purchaseEnergy(vibecoinAmount: number): Promise<{
  energyGained: number;
  vibecoinSpent: number;
  newEnergyBalance: number;
  newVibecoinBalance: number;
}> {
  return fetchEnergy('/api/energy/purchase', {
    method: 'POST',
    body: JSON.stringify({ vibecoin_amount: vibecoinAmount }),
  });
}

export async function getEnergyHistory(limit = 20, offset = 0): Promise<{
  logs: EnergyLogEntry[];
  total: number;
}> {
  return fetchEnergy(`/api/energy/history?limit=${limit}&offset=${offset}`);
}
